import { oktaAuth } from "../../auth/services/oktaAuth";
import {
  FRONTEND_LOG_FLUSH_MS,
  FRONTEND_LOG_MAX_BATCH_BYTES,
  FRONTEND_LOG_MAX_BATCH_ENTRIES,
  FRONTEND_LOG_MAX_QUEUE,
  FRONTEND_LOG_RETRY_MS,
} from "../const/frontend-logs";
import type { FrontendLogEntry } from "../types/frontend-logs";
import type { LocalLogWindow } from "../types/local-logs";
import { browserSessionId, getFrontendErrorFields } from "../utils/frontend-logs";
import { logE, setDebugLogSink } from "./debug-logger";

// Local console capture already delivers these records to frontend.log. Deployed
// collection uses authenticated fetch directly to avoid recursive Axios logging.
if (!(window as LocalLogWindow).__opslinkLocalLogCapture) {
  let queue: FrontendLogEntry[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight = false;
  let stopped = false;
  let retryAt = 0;
  let includeBrowserSessionId = true;
  const schedule = (delay = FRONTEND_LOG_FLUSH_MS) => {
    if (!timer && !stopped)
      timer = setTimeout(
        () => {
          timer = undefined;
          void flush();
        },
        Math.max(delay, retryAt - Date.now()),
      );
  };
  const flush = async () => {
    if (stopped || inFlight || !queue.length) return;
    const token = oktaAuth.getAccessToken();
    if (!token) {
      schedule(FRONTEND_LOG_RETRY_MS);
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      queue = [];
      return;
    }
    inFlight = true;
    const entries: FrontendLogEntry[] = [];
    for (const entry of queue.slice(0, FRONTEND_LOG_MAX_BATCH_ENTRIES)) {
      if (
        new TextEncoder().encode(JSON.stringify({ browserSessionId, entries: [...entries, entry] })).length >
        FRONTEND_LOG_MAX_BATCH_BYTES
      )
        break;
      entries.push(entry);
    }
    queue.splice(0, entries.length);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    let delay = FRONTEND_LOG_FLUSH_MS;
    try {
      const response = await fetch(`${apiUrl.replace(/\/$/, "")}/frontend-logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "omit",
        body: JSON.stringify(includeBrowserSessionId ? { browserSessionId, entries } : { entries }),
        signal: controller.signal,
        keepalive: true,
      });
      if ([403, 404].includes(response.status)) {
        stopped = true;
        queue = [];
        return;
      }
      if (!response.ok) {
        if (response.status === 400 && includeBrowserSessionId) {
          // Older intakes reject additional batch keys. Retry the same bounded
          // records with their original contract during a rolling release.
          includeBrowserSessionId = false;
          queue = [...entries, ...queue].slice(-FRONTEND_LOG_MAX_QUEUE);
        }
        // Reject malformed/oversized batches; retain transient failures only.
        if (response.status === 401 || response.status === 429 || response.status >= 500) {
          queue = [...entries, ...queue].slice(-FRONTEND_LOG_MAX_QUEUE);
          delay = FRONTEND_LOG_RETRY_MS;
          if (response.status === 429)
            delay = Math.max(delay, Math.min(300000, Number(response.headers.get("Retry-After")) * 1000 || 0));
        }
      }
    } catch {
      queue = [...entries, ...queue].slice(-FRONTEND_LOG_MAX_QUEUE);
      delay = FRONTEND_LOG_RETRY_MS;
    } finally {
      clearTimeout(timeout);
      inFlight = false;
      if (delay >= FRONTEND_LOG_RETRY_MS) {
        retryAt = Date.now() + delay;
        clearTimeout(timer);
        timer = undefined;
      }
      if (queue.length) schedule(delay);
    }
  };
  setDebugLogSink((level, message) => {
    if (stopped) return;
    const last =
      level === "error"
        ? queue.find((entry) => entry.level === level && entry.message === message && entry.occurrences < 1000)
        : queue[queue.length - 1];
    if (last?.message === message && last.level === level && last.occurrences < 1000) last.occurrences++;
    else queue.push({ timestamp: new Date().toISOString(), level, message, occurrences: 1 });
    if (queue.length > FRONTEND_LOG_MAX_QUEUE) queue.splice(0, queue.length - FRONTEND_LOG_MAX_QUEUE);
    schedule();
  });
  window.addEventListener("pagehide", () => {
    if (Date.now() >= retryAt) void flush();
  });
  window.addEventListener("error", (event: ErrorEvent) => {
    logE(event.error, { operation: "main - SUB-E error", line: event.lineno || undefined });
  });
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const fields = getFrontendErrorFields(event.reason);
    if (fields.code === "ERR_CANCELED" || fields.errorType === "AbortError" || fields.errorType === "CanceledError")
      return;
    logE(event.reason, { operation: "main - SUB-E unhandledrejection" });
  });
}
